import { AbsoluteFill } from 'remotion'
import ZealousSvg from '../assets/ZealousSvg'
import { FallbackImg } from '../components/FallbackImg'
import { BarsVisualization } from '../components/waveform/BarsVisualization'
import { LayoutInfo, LayoutProps } from '../types'
import { defaultClipScene } from '../types/defaultClipScene'
import { cn } from '../utils/cn'
import { useOrientation } from '../utils/useOrientation'
import { sceneAudioSrc } from '../utils/sceneAudioSrc'

function ScriptLarge({
  clipScene,
  children,
  orientation,
  speaker,
  style,
}: LayoutProps) {
  const { p, s, l, lp, sp, sl } = useOrientation(orientation)

  return (
    <AbsoluteFill
      className={cn(
        p && 'space-y-[60px]',
        s && 'space-y-[48px]',
        l && '!flex-row p-[90px]',
        sp && 'p-[60px]',
      )}
    >
      <div
        className={cn(
          'flex items-center',
          l && 'w-[585px] shrink-0 flex-col items-start space-y-[60px]',
          sp && 'space-x-[64px]',
        )}
      >
        <FallbackImg
          className={cn(
            'aspect-square h-[206px] rounded-full object-cover',
            lp && 'h-[234px]',
          )}
          src={speaker?.avatarUrl}
        />

        <div>
          <p
            style={{ ...style.speakerName }}
            className={cn(
              'line-clamp-1 text-left text-[60px] leading-[96px]',
              l && 'leading-[102px]',
            )}
          >
            {speaker?.name}
          </p>

          <p
            style={{ ...style.speakerUsername }}
            className={cn(
              'line-clamp-1 text-left text-[56px] leading-[89px]',
              l && 'leading-[95px]',
            )}
          >
            {speaker?.username}
          </p>
        </div>
      </div>

      <div
        className={cn(
          p && 'space-y-[80px]',
          s && 'space-y-[50px]',
          l && 'space-y-[60px]',
        )}
      >
        <div className="">{children}</div>

        <div
          className="h-[2px] w-full"
          style={{ background: style.border.color, opacity: 0.5 }}
        />

        <div
          className={cn(
            'flex items-center',
            l && 'space-x-[60px]',
            sp && 'space-x-[40px]',
          )}
        >
          <FallbackImg
            className="aspect-square h-[116px] rounded-full object-cover"
            src={clipScene.imageUrl}
          />

          <div className={cn('text-[40px] leading-[59px]')}>
            <p style={style.title} className="line-clamp-1 text-left">
              {clipScene.title}
            </p>

            <p style={style.subtitle} className="line-clamp-1 text-left">
              {clipScene.subtitle}
            </p>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 w-full">
        <BarsVisualization
          color={style.waveform.color}
          gapSize={0}
          height={
            orientation === 'portrait'
              ? 600
              : orientation === 'landscape'
                ? 400
                : 300
          }
          lineThickness={20}
          mediaUrl={sceneAudioSrc(clipScene)}
          waveformUrl={clipScene.waveformUrl}
          mediaStart={clipScene.mediaStart}
          placement="under"
          roundness={0}
        />
      </div>

      {clipScene.showZealousLogo && (
        <ZealousSvg
          color={style.logo.color}
          className={cn(
            'absolute bottom-[50px] left-[60px] h-[49px] opacity-50',
            l && 'bottom-[60px]',
          )}
        />
      )}
    </AbsoluteFill>
  )
}

export const scriptLargeLayoutInfo: LayoutInfo = {
  name: 'scriptLarge',
  Component: ScriptLarge,
  defaultScene: {
    ...defaultClipScene,
    layout: 'scriptLarge',
  },
  defaultStyle: {
    background: { color: 'primary' },
    border: { color: 'secondary' },
    logo: { color: 'primary' },
    waveform: { color: 'accent' },
    subtitle: {
      color: 'secondary',
      fontFamily: 'primary',
      opacity: 0.5,
    },
    title: {
      color: 'secondary',
      fontFamily: 'primary',
    },
    speakerUsername: {
      color: 'secondary',
      fontFamily: 'primary',
      opacity: 0.5,
    },
    speakerName: { color: 'secondary', fontFamily: 'primary' },
    transcription: {
      highlightColor: 'primary',
      highlightBgColor: 'accent',
      color: 'secondary',
      scrollType: 'line-by-line',
      fontFamily: 'primary',
      highlightType: 'last-word-bg',
      fontSize: 54,
      fontWeight: 500,
      linesPerPage: 3,
      lineHeight: 1.5,
      textAlign: 'left',
    },
  },
  transcriptions: {
    square: {},
    landscape: {
      fontSize: 60,
      lineHeight: 1.6,
      linesPerPage: 4,
    },
    portrait: {
      fontSize: 74,
      lineHeight: 1.7,
      linesPerPage: 6,
    },
  },
}
